import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {Store} from '@ngrx/store';
import {fromEvent, interval, merge, Subscription, timer} from 'rxjs';
import {debounceTime, share, switchMap, takeUntil, tap} from 'rxjs/operators';
import {TimeEnum} from '../models';
import {heartBeatApplicationOnUserActivity, heartBeatInactivityTriggered} from '../store-heartbeat/heartbeat.actions';

/**
 * Service to monitor user activity and dispatch periodic heartbeat actions.
 *
 * This service listens to various user interaction events (mouse, keyboard, scroll, etc.)
 * and starts a heartbeat timer that dispatches a heartbeat action every `heartbeatIntervalInMs`
 * milliseconds *only while* the user is active.
 *
 * The heartbeat stops if user inactivity exceeds `userInactivityTimeoutInMs`.
 *
 * @remarks
 * Uses Angular `NgZone` to run timers outside Angular change detection
 * and re-enters Angular zone only when dispatching actions.
 */
@Injectable({
  providedIn: 'root',
})
export class HeartbeatService implements OnDestroy {

  /**
   * Interval in milliseconds between heartbeat dispatches while user is active.
   */
  private readonly heartbeatIntervalInMs = TimeEnum.TwoMinutes;

  /**
   * Debounce time in milliseconds for user activity events.
   * Prevents rapid successive events from triggering multiple heartbeats.
   */
  private readonly userActivityDebounceInMs = 300;

  /**
   * Duration in milliseconds after which the user is considered inactive
   * and the heartbeat stops.
   */
  private readonly userInactivityTimeoutInMs = TimeEnum.TwentyMinutes;

  /**
   * Timestamp of the last detected user activity.
   */
  private userLastActivityTimestamp = Date.now();

  /**
   * Flag to prevent multiple initializations of the heartbeat flow.
   */
  private heartbeatStarted = false;

  /**
   * Subscription container for all internal subscriptions to events and timers.
   */
  private subscription = new Subscription();

  /**
   * Creates an instance of HeartbeatService.
   *
   * @param store NgRx Store used to dispatch heartbeat and inactivity actions.
   * @param ngZone Angular NgZone used to manage running code inside/outside Angular's zone.
   */
  constructor(
    private readonly store: Store,
    private ngZone: NgZone,
  ) {
  }

  /**
   * Angular lifecycle hook called when the service is destroyed.
   * Cleans up all active subscriptions.
   */
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Starts the heartbeat mechanism, which dispatches a heartbeat action every
   * `heartbeatIntervalInMs` milliseconds *only when* user activity is detected.
   *
   * Calling this method multiple times will have no effect after the first start.
   */
  public start(): void {
    if (this.heartbeatStarted) return;
    this.heartbeatStarted = true;
    this.initializeUserActivityHeartbeatFlow();
  }

  /**
   * Initializes the heartbeat mechanism that listens to user activity events,
   * starts a heartbeat interval on activity, and stops after prolonged inactivity.
   *
   * The heartbeat resets on every new user activity event and stops after
   * `userInactivityTimeoutInMs` milliseconds of inactivity.
   *
   * Uses Angular NgZone to run the timer outside Angular change detection
   * for performance, and re-enters zone only when dispatching actions.
   */
  private initializeUserActivityHeartbeatFlow(): void {
    const events$ = merge(
      fromEvent(window, 'mousemove'),
      fromEvent(window, 'keydown'),
      fromEvent(window, 'scroll'),
      fromEvent(window, 'click'),
      fromEvent(window, 'load'),
      fromEvent(window, 'touchstart'),
    ).pipe(share());

    const userActivity$ = events$.pipe(
      debounceTime(this.userActivityDebounceInMs),
      tap(() => {
        this.userLastActivityTimestamp = Date.now();
      }),
      share(),
    );

    this.ngZone.runOutsideAngular(() => {
      const heartbeat$ = userActivity$.pipe(
        switchMap(() =>
          interval(this.heartbeatIntervalInMs).pipe(
            tap(() => {
              const now = Date.now();
              this.store.dispatch(heartBeatApplicationOnUserActivity({heartBeat: now}));

              const timeSinceLastActivity = now - this.userLastActivityTimestamp;
              if (timeSinceLastActivity >= this.userInactivityTimeoutInMs) {
                this.ngZone.run(() => {
                  this.store.dispatch(heartBeatInactivityTriggered());
                });
              }
            }),
            takeUntil(timer(this.userInactivityTimeoutInMs)),
          ),
        ),
      );

      this.subscription.add(userActivity$.subscribe());
      this.subscription.add(heartbeat$.subscribe());
    });
  }
}
